import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, BookOpen, Maximize2, Minimize2, Loader2 } from 'lucide-react';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { AudioButton } from '../components/AudioButton';
import { CourseQuiz } from '../components/CourseQuiz';
import { ExerciseSection } from '../components/ExerciseSection';
import { toast } from 'sonner';
import { useCourseContent } from '../hooks/useContentData';
import { GrammatikSection } from '../components/GrammatikSection';
import { A2_1_ANSWERS, A2_1_SECTIONS } from '../data/quizBank';
import EXERCISES_A21 from '../data/exerciseBankA21';

export default function MenschenA21() {
  const [activeTab, setActiveTab] = useState('l1');
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [bookUrl, setBookUrl] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState('kursbuch');
  const { lessons, grammatik: grammatikA21, loading } = useCourseContent('menschen_a21');
  const API = process.env.REACT_APP_BACKEND_URL;

  const BOOKS_A21 = {
    kursbuch: { id: 'menschen_a21_kursbuch', label: 'Kursbuch', labelEn: 'Textbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/f4754dec3f530f88f1916477fa27034b/edureader/app/pdf/lib/web/viewer.html' },
    arbeitsbuch: { id: 'menschen_a21_arbeitsbuch', label: 'Arbeitsbuch', labelEn: 'Workbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/0fba535634e75b6210197b31030f7de7/edureader/app/pdf/lib/web/viewer.html' },
  };

  const fetchBookUrl = async (bookType) => {
    const book = BOOKS_A21[bookType || selectedBook];
    setBookLoading(true);
    setBookUrl(null);
    try {
      const res = await fetch(`${API}/api/books/${book.id}/url`);
      const data = await res.json();
      setBookUrl(data.viewer_url || book.url);
    } catch { setBookUrl(book.url); }
    finally { setBookLoading(false); }
  };

  const tabs = [
    { id: 'mod1', label: 'Modul 1', icon: '📘', isHeader: true },
    { id: 'l1', label: 'L1: Familie', icon: '👨‍👩‍👧' },
    { id: 'l2', label: 'L2: Umzug', icon: '📦' },
    { id: 'l3', label: 'L3: Feiern', icon: '🎉' },
    { id: 'g1', label: 'Grammatik 1-3', icon: '📐', isGrammatik: true },
    { id: 'mod2', label: 'Modul 2', icon: '📗', isHeader: true },
    { id: 'l4', label: 'L4: Gefühle', icon: '😊' },
    { id: 'l5', label: 'L5: Arbeit', icon: '💼' },
    { id: 'l6', label: 'L6: Mobilität', icon: '🚆' },
    { id: 'g2', label: 'Grammatik 4-6', icon: '📐', isGrammatik: true },
    { id: 'mod3', label: 'Modul 3', icon: '📙', isHeader: true },
    { id: 'l7', label: 'L7: Natur', icon: '🏕️' },
    { id: 'l8', label: 'L8: Freude', icon: '🎊' },
    { id: 'l9', label: 'L9: Termine', icon: '📅' },
    { id: 'g3', label: 'Grammatik 7-9', icon: '📐', isGrammatik: true },
    { id: 'mod4', label: 'Modul 4', icon: '📕', isHeader: true },
    { id: 'l10', label: 'L10: Zufall', icon: '🎲' },
    { id: 'l11', label: 'L11: Wohnen', icon: '🏠' },
    { id: 'l12', label: 'L12: Zukunft', icon: '🌟' },
    { id: 'g4', label: 'Grammatik 10-12', icon: '📐', isGrammatik: true },
    { id: 'pdf', label: 'Interaktives Lernen', icon: '📖', isPdf: true },
    { id: 'exercises', label: 'Übungen', icon: '🏋️', isExercise: true },
    { id: 'quiz', label: 'Quiz', icon: '✅' },
  ];

  const lessonMap = {
    l1: lessons['lektion1'], l2: lessons['lektion2'], l3: lessons['lektion3'],
    l4: lessons['lektion4'], l5: lessons['lektion5'], l6: lessons['lektion6'],
    l7: lessons['lektion7'], l8: lessons['lektion8'], l9: lessons['lektion9'],
    l10: lessons['lektion10'], l11: lessons['lektion11'], l12: lessons['lektion12'],
  };

  const courseContents = {
    l1: { grammar: 'Pr\u00e4teritum von sein/haben', topics: ['Family & professions', 'Talking about the past', 'Childhood memories'], grammarAr: '\u0627\u0644\u0645\u0627\u0636\u064a \u0627\u0644\u0628\u0633\u064a\u0637' },
    l2: { grammar: 'Wechselpr\u00e4positionen', topics: ['Furniture & rooms', 'Giving directions (Wohin?)', 'Describing positions (Wo?)'], grammarAr: '\u062d\u0631\u0648\u0641 \u0627\u0644\u062c\u0631 \u0627\u0644\u0645\u062a\u0628\u062f\u0644\u0629' },
    l3: { grammar: 'Konjunktiv II mit w\u00fcrde', topics: ['Party planning', 'Expressing wishes', 'Polite requests'], grammarAr: '\u0627\u0644\u0634\u0631\u0637\u064a\u0629' },
    l4: { grammar: 'Reflexivpronomen + weil', topics: ['Emotions & feelings', 'Reflexive verbs', 'Giving reasons'], grammarAr: '\u0627\u0644\u0636\u0645\u0627\u0626\u0631 \u0627\u0644\u0627\u0646\u0639\u0643\u0627\u0633\u064a\u0629' },
    l5: { grammar: 'Nebens\u00e4tze mit dass', topics: ['Home office vs office', 'Expressing opinions', 'Work-life balance'], grammarAr: '\u062c\u0645\u0644 dass' },
    l6: { grammar: 'Komparativ & Superlativ', topics: ['Transportation', 'Comparing things', 'At the train station'], grammarAr: '\u0627\u0644\u0645\u0642\u0627\u0631\u0646\u0629 \u0648\u0627\u0644\u062a\u0641\u0636\u064a\u0644' },
    l7: { grammar: 'k\u00f6nnte/sollte + wenn', topics: ['Nature & camping', 'Making suggestions', 'Weather'], grammarAr: '\u0627\u0644\u0627\u0642\u062a\u0631\u0627\u062d\u0627\u062a' },
    l8: { grammar: 'Verben + Pr\u00e4positionen', topics: ['Congratulations', 'Joy & sympathy', 'auf vs \u00fcber'], grammarAr: '\u0623\u0641\u0639\u0627\u0644 + \u062d\u0631\u0648\u0641 \u062c\u0631' },
    l9: { grammar: 'Temporale Pr\u00e4p. + ob', topics: ['Doctor appointments', 'At the office', 'Cancelling/rescheduling'], grammarAr: '\u062d\u0631\u0648\u0641 \u0627\u0644\u062c\u0631 \u0627\u0644\u0632\u0645\u0646\u064a\u0629' },
    l10: { grammar: 'als vs. wenn', topics: ['Telling stories', 'Coincidences', 'Past vs repeated events'], grammarAr: 'als \u0645\u0642\u0627\u0628\u0644 wenn' },
    l11: { grammar: 'Adjektivdeklination', topics: ['Apartment hunting', 'WG life', 'Describing rooms'], grammarAr: '\u062a\u0635\u0631\u064a\u0641 \u0627\u0644\u0635\u0641\u0627\u062a' },
    l12: { grammar: 'Futur I: werden + Inf.', topics: ['Future plans', 'Goals & dreams', 'Motivation'], grammarAr: '\u0627\u0644\u0645\u0633\u062a\u0642\u0628\u0644' },
  };

  const exerciseKeys = ['L1','L2','L3','L4','L5','L6','L7','L8','L9','L10','L11','L12'];
  const exerciseLabels = ['L1: Familie','L2: Umzug','L3: Feiern','L4: Gefühle','L5: Arbeit','L6: Mobilität','L7: Natur','L8: Freude','L9: Termine','L10: Zufall','L11: Wohnen','L12: Zukunft'];

  const handleTranscription = (text) => { if (text) toast.info(`You said: "${text}"`); };
  const PracticeButton = ({ text }) => (
    <div className="inline-flex items-center gap-1">
      <AudioButton text={text} size="sm" voice="nova" />
      <MicrophoneButton size="sm" expectedText={text} onTranscription={handleTranscription} onPronunciationScore={() => {}} />
    </div>
  );

  const renderModuleTab = (tabId, moduleData) => {
    const cc = courseContents[tabId];
    return (
    <div className={`section ${activeTab === tabId ? 'active' : ''}`} data-testid={`tab-${tabId}`}>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>{moduleData.title.de}</h2>
      <p style={{ color: '#94a3b8', marginBottom: '12px' }}>{moduleData.title.en} · <span className="ar">{moduleData.title.ar}</span></p>

      {/* Course Contents Box */}
      {cc && (
        <div style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(59,130,246,0.03))', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }} data-testid={`course-contents-${tabId}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#60a5fa' }}>Kursinhalt / Course Contents</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}>
              Grammatik: {cc.grammar}
            </span>
            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)', direction: 'rtl' }}>
              {cc.grammarAr}
            </span>
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.8' }}>
            {cc.topics.map((t, i) => <li key={`t-${tabId}-${i}`}>{t}</li>)}
          </ul>
        </div>
      )}

      {moduleData.sections.map((section, si) => {
        const sKey = `${tabId}-${section.type}-${si}`;
        if (section.type === 'vocab_table') {
          return (
            <div key={sKey} className="card">
              <div className="card-header"><span className="card-icon">{section.icon}</span><h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3></div>
              <div className="card-body">
                <table className="vocab-table">
                  <thead><tr><th>Deutsch</th><th>English</th><th className="ar">عربي</th>{section.hasAudio && <th>Üben</th>}</tr></thead>
                  <tbody>
                    {section.items.map((item, i) => (
                      <tr key={`r-${i}-${item.de?.slice(0,8)}`}>
                        <td className="de">{item.de}</td><td className="en">{item.en}</td><td className="ar">{item.ar}</td>
                        {section.hasAudio && <td><PracticeButton text={item.de} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (section.type === 'grammar_table') {
          return (
            <div key={sKey} className="card">
              <div className="card-header"><span className="card-icon">{section.icon}</span><h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3></div>
              <div className="card-body"><div className="grammar-box"><table className="grammar-table">
                <thead><tr>{section.headers.map((h, hi) => <th key={`h-${h}-${hi}`} className={h === 'عربي' ? 'ar' : ''}>{h}</th>)}</tr></thead>
                <tbody>{section.rows.map((row, ri) => (<tr key={`row-${ri}-${row[0]}`}>{row.map((cell, ci) => (<td key={`c-${ri}-${ci}`} className={ci === 1 ? 'em' : ci === row.length - 1 ? 'ar' : ''}>{cell}</td>))}</tr>))}</tbody>
              </table></div></div>
            </div>
          );
        }
        if (section.type === 'phrase_grid') {
          return (
            <div key={sKey} className="card">
              <div className="card-header"><span className="card-icon">{section.icon}</span><h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3></div>
              <div className="card-body"><div className="phrase-grid">
                {section.items.map((item, i) => (
                  <div key={`p-${item.ctx}-${i}`} className="phrase-card">
                    <div className="phrase-ctx">{item.ctx}</div>
                    <div className="phrase-de flex items-center justify-between"><span>{item.de}</span><PracticeButton text={item.de} /></div>
                    <div className="phrase-en">{item.en}</div><div className="phrase-ar">{item.ar}</div>
                  </div>
                ))}
              </div></div>
            </div>
          );
        }
        if (section.type === 'dialog') {
          return (
            <div key={sKey} className="dialog-box">
              <div className="dialog-title">{section.title}</div>
              <div className="dialog-content">
                {section.lines.map((line, j) => (
                  <div key={`dl-${line.speaker}-${j}`} className="dialog-line">
                    <div className="dialog-speaker" style={{ color: j % 2 === 0 ? '#3b82f6' : '#f59e0b' }}>{line.speaker}:</div>
                    <div className={`dialog-bubble ${j % 2 !== 0 ? 'patient' : ''}`}>
                      <div className="de" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span>{line.de}</span><PracticeButton text={line.de} /></div>
                      <div className="en">{line.en}</div><div className="ar">{line.ar}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (section.type === 'tip') {
          return (
            <div key={sKey} className="tip-box" style={{ marginTop: '16px' }}>
              <strong>Tipp · Tip · <span className="ar">نصيحة</span></strong><br />
              {section.text.de} · {section.text.en} · <span className="ar">{section.text.ar}</span>
            </div>
          );
        }
        return null;
      })}
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="menschen-a21-page">
      <style>{`
        .ma21-container { max-width: 900px; margin: 0 auto; padding: 20px; }
        .ma21-header { text-align: center; padding: 30px 0; border-bottom: 1px solid #334155; margin-bottom: 20px; }
        .ma21-header h1 { font-size: 28px; font-weight: 700; color: #3b82f6; margin-bottom: 8px; }
        .ma21-header p { color: #94a3b8; font-size: 14px; }
        .ma21-header .modul-badges { display: flex; justify-content: center; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
        .ma21-header .modul-badge { padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .tab-nav { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px; justify-content: center; }
        .tab-btn { padding: 10px 16px; border-radius: 8px; background: #1e293b; border: 1px solid #334155; color: #94a3b8; cursor: pointer; transition: all 0.2s; font-size: 13px; }
        .tab-btn:hover { background: #334155; }
        .tab-btn.active { background: #3b82f6; color: white; border-color: #3b82f6; }
        .tab-btn.module-header { background: transparent; border: none; color: #60a5fa; font-weight: 700; cursor: default; font-size: 13px; padding: 10px 6px; }
        .tab-btn.grammatik-tab { border-color: rgba(167,139,250,0.4); color: #a78bfa; }
        .tab-btn.grammatik-tab.active { background: #a78bfa; color: white; }
        .tab-btn.pdf-tab { border-color: rgba(245,158,11,0.4); color: #f59e0b; }
        .tab-btn.pdf-tab.active { background: #f59e0b; color: white; }
        .tab-btn.exercise-tab { border-color: rgba(20,184,166,0.4); color: #14b8a6; }
        .tab-btn.exercise-tab.active { background: #14b8a6; color: white; }
        .section { display: none; } .section.active { display: block; }
        .card { background: #1e293b; border: 1px solid #334155; border-radius: 12px; margin-bottom: 20px; overflow: hidden; }
        .card-header { padding: 16px 20px; border-bottom: 1px solid #334155; display: flex; align-items: center; gap: 12px; }
        .card-icon { font-size: 24px; }
        .card-header h3 { font-size: 16px; font-weight: 600; color: #e2e8f0; }
        .card-body { padding: 20px; }
        .grammar-tag { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; background: rgba(167,139,250,0.15); color: #a78bfa; border: 1px solid rgba(167,139,250,0.3); margin-top: 8px; }
        .vocab-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .vocab-table th { background: #0f172a; padding: 12px; text-align: left; font-weight: 600; color: #94a3b8; }
        .vocab-table td { padding: 12px; border-bottom: 1px solid #334155; }
        .vocab-table .de { color: #f1f5f9; font-weight: 500; }
        .vocab-table .en { color: #94a3b8; }
        .vocab-table .ar { color: #3b82f6; direction: rtl; text-align: right; }
        .phrase-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
        .phrase-card { background: #0f172a; border-radius: 8px; padding: 12px; }
        .phrase-ctx { font-size: 11px; color: #3b82f6; text-transform: uppercase; margin-bottom: 4px; }
        .phrase-de { font-size: 15px; font-weight: 600; color: #f1f5f9; }
        .phrase-en { font-size: 13px; color: #94a3b8; }
        .phrase-ar { font-size: 13px; color: #3b82f6; direction: rtl; }
        .grammar-box { background: #0f172a; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
        .grammar-table { width: 100%; font-size: 13px; }
        .grammar-table th { background: #1e293b; padding: 10px; text-align: left; color: #94a3b8; }
        .grammar-table td { padding: 10px; border-bottom: 1px solid #334155; }
        .grammar-table .em { color: #f59e0b; font-weight: 600; }
        .grammar-table .ar { color: #3b82f6; direction: rtl; }
        .dialog-box { background: #1e293b; border: 1px solid #334155; border-radius: 12px; margin-bottom: 16px; overflow: hidden; }
        .dialog-title { background: #0f172a; padding: 12px 16px; font-weight: 600; font-size: 14px; color: #3b82f6; }
        .dialog-content { padding: 16px; }
        .dialog-line { display: flex; gap: 12px; margin-bottom: 12px; }
        .dialog-speaker { min-width: 80px; font-weight: 600; font-size: 12px; padding-top: 4px; }
        .dialog-bubble { flex: 1; background: #0f172a; border-radius: 8px; padding: 10px 14px; }
        .dialog-bubble.patient { background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.3); }
        .dialog-bubble .de { font-weight: 500; color: #f1f5f9; margin-bottom: 4px; }
        .dialog-bubble .en { font-size: 12px; color: #94a3b8; }
        .dialog-bubble .ar { font-size: 12px; color: #3b82f6; direction: rtl; }
        .tip-box { background: linear-gradient(135deg, rgba(59,130,246,0.1), rgba(59,130,246,0.05)); border: 1px solid rgba(59,130,246,0.3); border-radius: 8px; padding: 14px 16px; margin-top: 16px; }
        .tip-box strong { color: #3b82f6; }
        .tab-btn.grammatik-tab { border-color: rgba(167,139,250,0.4); color: #a78bfa; }
        .tab-btn.grammatik-tab.active { background: #a78bfa; color: white; }
        .pdf-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: linear-gradient(135deg, #1e293b, #0f172a); border: 1px solid rgba(245,158,11,0.3); border-radius: 12px 12px 0 0; }
        .pdf-header-left { display: flex; align-items: center; gap: 12px; }
        .pdf-actions { display: flex; gap: 8px; }
        .pdf-action-btn { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; background: rgba(245,158,11,0.15); color: #f59e0b; border: 1px solid rgba(245,158,11,0.3); cursor: pointer; transition: all 0.2s; text-decoration: none; }
        .pdf-action-btn:hover { background: rgba(245,158,11,0.3); }
        .pdf-iframe-container { border: 1px solid rgba(245,158,11,0.2); border-top: none; border-radius: 0 0 12px 12px; overflow: hidden; background: #0f172a; }
        .pdf-iframe { width: 100%; height: 75vh; border: none; display: block; }
        .pdf-fullscreen { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #0f172a; padding: 0; }
        .pdf-fullscreen .pdf-header { border-radius: 0; }
        .pdf-fullscreen .pdf-iframe-container { border-radius: 0; border: none; }
        .pdf-fullscreen .pdf-iframe { height: calc(100vh - 68px); }
        .pdf-fallback { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 40px 20px; background: linear-gradient(135deg, #1e293b, #0f172a); border-top: 1px solid rgba(245,158,11,0.1); }
        .pdf-open-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 28px; background: #f59e0b; color: #0f172a; font-weight: 600; font-size: 15px; border-radius: 10px; text-decoration: none; transition: all 0.2s; cursor: pointer; border: none; }
        .pdf-open-btn:hover { background: #fbbf24; transform: translateY(-1px); }
        @media (max-width: 640px) { .ma21-container { padding: 12px; } .tab-btn { padding: 8px 12px; font-size: 12px; } }
      `}</style>

      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-link">
            <ChevronLeft className="h-5 w-5" /><span className="text-sm">Back</span>
          </Link>
          <h1 className="text-blue-500 font-bold">Menschen A2.1</h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="ma21-container">
        <div className="ma21-header">
          <h1>Menschen A2.1</h1>
          <p>German for Pre-Intermediate – Part 1 · <span style={{ color: '#3b82f6' }}>الألمانية للمتوسط المبتدئ – الجزء الأول</span></p>
          <div className="modul-badges">
            <span className="modul-badge" style={{ background: 'rgba(59,130,246,0.2)', color: '#60a5fa' }}>Modul 1–4</span>
            <span className="modul-badge" style={{ background: 'rgba(96,165,250,0.2)', color: '#93c5fd' }}>12 Lektionen</span>
            <span className="modul-badge" style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>A2.1 Level</span>
          </div>
        </div>

        <div className="tab-nav" data-testid="tab-navigation">
          {tabs.map(tab => {
            if (tab.isHeader) return <span key={tab.id} className="tab-btn module-header">{tab.icon} {tab.label}</span>;
            return (
              <button key={tab.id} data-testid={`tab-btn-${tab.id}`}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${tab.isPdf ? 'pdf-tab' : ''} ${tab.isExercise ? 'exercise-tab' : ''} ${tab.isGrammatik ? 'grammatik-tab' : ''}`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.icon} {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
        ) : (
          <>
            {/* Lesson Tabs - Dynamic from backend */}
            {Object.entries(lessonMap).filter(([, data]) => data).map(([tabId, data]) => (
              <React.Fragment key={tabId}>{renderModuleTab(tabId, data)}</React.Fragment>
            ))}

            {/* Grammatik Tabs */}
            {['g1','g2','g3','g4'].map((gid, gi) => {
              const keys = [['l1','l2','l3'],['l4','l5','l6'],['l7','l8','l9'],['l10','l11','l12']][gi];
              return (
                <div key={gid} className={`section ${activeTab === gid ? 'active' : ''}`} data-testid={`tab-${gid}`}>
                  <GrammatikSection items={keys.map(k => grammatikA21[k]).filter(Boolean)} accentColor="#3b82f6" />
                </div>
              );
            })}
          </>
        )}

        {/* Quiz Tab */}
        <div className={`section ${activeTab === 'quiz' ? 'active' : ''}`} data-testid="tab-quiz">
          <CourseQuiz quizId="menschen_a21_quiz" courseId="menschen_a21" title="Menschen A2.1 – Abschlussquiz" sections={A2_1_SECTIONS} answers={A2_1_ANSWERS} isActive={true} />
        </div>

        {/* Exercises Tab */}
        {activeTab === 'exercises' && (
          <div className="section active" data-testid="tab-exercises">
            <ExerciseLessonPicker exercises={EXERCISES_A21} exerciseKeys={exerciseKeys} labels={exerciseLabels} />
          </div>
        )}

        {/* PDF Interactive Learning Tab */}
        <div className={`section ${activeTab === 'pdf' ? 'active' : ''}`} data-testid="tab-pdf">
          {activeTab === 'pdf' && (
            <div className={pdfFullscreen ? 'pdf-fullscreen' : ''}>
              <div className="pdf-header">
                <div className="pdf-header-left">
                  <BookOpen className="h-5 w-5" style={{ color: '#f59e0b' }} />
                  <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Interaktives Lernen – Menschen A2.1</h2>
                    <p style={{ fontSize: '13px', color: '#94a3b8', margin: '2px 0 0' }}>Interactive Textbook · الكتاب التفاعلي</p>
                  </div>
                </div>
                <div className="pdf-actions">
                  {bookUrl && (
                    <>
                      <button className="pdf-action-btn" onClick={() => setPdfFullscreen(!pdfFullscreen)} data-testid="pdf-fullscreen-toggle">
                        {pdfFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                      </button>
                      <a href={bookUrl} target="_blank" rel="noopener noreferrer" className="pdf-action-btn" data-testid="pdf-new-tab">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </a>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', background: '#0f172a', borderLeft: '1px solid rgba(245,158,11,0.2)', borderRight: '1px solid rgba(245,158,11,0.2)' }}>
                {Object.entries(BOOKS_A21).map(([key, book]) => (
                  <button key={key} onClick={() => { setSelectedBook(key); setBookUrl(null); }}
                    style={{ padding: '8px 20px', borderRadius: '8px', border: selectedBook === key ? '2px solid #f59e0b' : '1px solid #334155', background: selectedBook === key ? 'rgba(245,158,11,0.15)' : '#1e293b', color: selectedBook === key ? '#f59e0b' : '#94a3b8', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    data-testid={`book-select-${key}`}>
                    {key === 'kursbuch' ? '📕' : '📗'} {book.label} ({book.labelEn})
                  </button>
                ))}
              </div>
              {!bookUrl && !bookLoading && (
                <div className="pdf-fallback">
                  <BookOpen className="h-12 w-12" style={{ color: '#f59e0b', marginBottom: '16px' }} />
                  <p style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '17px', marginBottom: '8px' }}>{BOOKS_A21[selectedBook].label} – Menschen A2.1</p>
                  <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>Klicken Sie, um das {BOOKS_A21[selectedBook].label} zu laden.</p>
                  <button onClick={() => fetchBookUrl(selectedBook)} className="pdf-open-btn" data-testid="pdf-load-book">
                    <BookOpen className="h-4 w-4" /> {BOOKS_A21[selectedBook].label} laden
                  </button>
                </div>
              )}
              {bookLoading && (
                <div className="pdf-fallback">
                  <Loader2 className="h-10 w-10 animate-spin" style={{ color: '#f59e0b', marginBottom: '16px' }} />
                  <p style={{ color: '#e2e8f0', fontWeight: 600 }}>Buch wird geladen...</p>
                </div>
              )}
              {bookUrl && (
                <div className="pdf-iframe-container">
                  <iframe src={bookUrl} title={`Menschen A2.1 ${BOOKS_A21[selectedBook].label}`} className="pdf-iframe" allow="fullscreen" data-testid="pdf-iframe" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExerciseLessonPicker({ exercises, exerciseKeys, labels }) {
  const [selectedLesson, setSelectedLesson] = useState(exerciseKeys[0]);
  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>Übungen / Exercises</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        {exerciseKeys.map((key, i) => (
          <button key={key} onClick={() => setSelectedLesson(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedLesson === key ? 'bg-blue-600 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'}`}
            data-testid={`exercise-lesson-${key}`}>{labels[i]}</button>
        ))}
      </div>
      <ExerciseSection lessonId={selectedLesson} isActive={true} exercises={exercises} />
    </div>
  );
}
